import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Leaf, CircleNotch, Warning, CheckCircle } from "@phosphor-icons/react";
import InstallPrompt from "@/components/InstallPrompt";

const GRASS_BG =
  "https://images.unsplash.com/photo-1589496933738-f5c27bc146e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxncmFzcyUyMHRleHR1cmV8ZW58MHx8fHwxNzg2MjY0Njc2fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [verifiedOk, setVerifiedOk] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resent, setResent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [town, setTown] = useState("Mackay");
  const [townLine, setTownLine] = useState("Mackay, QLD");

  // Best-effort: reflect the visitor's closest town from their GPS location.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const r = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${coords.latitude}&longitude=${coords.longitude}&localityLanguage=en`);
          const d = await r.json();
          const city = d.city || d.locality || d.principalSubdivision;
          const region = d.principalSubdivisionCode?.split("-")?.[1] || d.principalSubdivision;
          if (city) {
            setTown(city);
            setTownLine(region && region !== city ? `${city}, ${region}` : city);
          }
        } catch (e) { console.error("Prefill business name failed:", e); /* keep default */ }
      },
      () => { /* permission denied — keep default */ },
      { timeout: 8000, maximumAge: 600000 }
    );
  }, []);

  useEffect(() => {
    const reason = params.get("reason");
    if (reason === "device")
      setNotice("You've been logged out because your account was accessed on another device.");
    else if (reason === "expired") setNotice("Session expired — please log in again.");
    else if (reason === "reset") setNotice("Password updated — please log in with your new password.");
    const verified = params.get("verified");
    if (verified === "1") setVerifiedOk(true);
    else if (verified === "error") setError("This verification link is invalid or has expired. Log in to resend a new one.");
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setResent(false);
    setBusy(true);
    try {
      await login(email, password, remember);
      navigate("/");
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(detail);
      setNeedsVerify(status === 403); // unverified NEW user → offer resend
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification", { email, base_url: window.location.origin });
    } catch (e) { console.error("Password reset request failed:", e); /* never reveals status */ }
    setResent(true);
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={GRASS_BG} alt="Fresh cut grass" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#2E2E2E]/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <img src="/icon.png" alt="MowGuru" className="w-11 h-11 rounded-md bg-white object-contain" />
            <span className="font-display font-black text-2xl">MowGuru</span>
          </div>
          <div>
            <h1 className="font-display font-black text-5xl leading-tight mb-4">
              MowGuru keeps your business running — anywhere you go.
            </h1>
            <p className="text-white/70 text-lg max-w-md" data-testid="login-town-weather">
              Jobs, clients, scheduling and live {town} weather — built for contractors who work outdoors.
            </p>
          </div>
          <p className="text-white/60 text-sm" data-testid="login-town-line">{townLine} · Field Service Command</p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-background grain">
        <div className="w-full max-w-md animate-fade-up">
          <div className="flex justify-center pt-2 pb-8">
            <img src="/logo.png" alt="MowGuru"
              data-testid="login-logo"
              className="w-[220px] sm:w-[260px] lg:w-[300px] h-auto object-contain select-none" />
          </div>
          <h2 className="font-display font-black text-3xl text-[#2E2E2E] mb-1">Welcome back</h2>
          <p className="text-muted-foreground mb-8">Log in to your contractor dashboard.</p>

          {verifiedOk && (
            <div data-testid="login-verified-ok"
              className="mb-5 flex items-start gap-2 rounded-md border border-[#3BB54A]/40 bg-[#EAF7EC] px-4 py-3 text-sm font-medium text-[#2E7D32]">
              <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0" /> Email verified — you can now log in.
            </div>
          )}

          {notice && (
            <div data-testid="login-notice"
              className="mb-5 flex items-start gap-2 rounded-md border border-[#FFC857]/50 bg-[#FFF6E0] px-4 py-3 text-sm font-medium text-[#7A5A00]">
              <Warning size={18} weight="fill" className="mt-0.5 shrink-0" /> {notice}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full h-14 px-4 rounded-md border border-border bg-white focus:outline-none focus:ring-2 focus:ring-[#3BB54A]"
                placeholder="you@business.com"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full h-14 px-4 rounded-md border border-border bg-white focus:outline-none focus:ring-2 focus:ring-[#3BB54A]"
                placeholder="••••••••"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me-label">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="remember-me-checkbox"
                  className="w-5 h-5 rounded border-border accent-[#2E2E2E] cursor-pointer"
                />
                <span className="text-sm font-medium text-[#212529]">Stay logged in for 30 days</span>
              </label>
              <Link to="/forgot-password" data-testid="forgot-password-link"
                className="text-sm font-semibold text-[#2E2E2E] hover:underline">
                Forgot password?
              </Link>
            </div>

            {error && (
              <p className="text-sm font-medium text-[#E63946]" data-testid="login-error">{error}</p>
            )}
            {needsVerify && (
              resent
                ? <p className="text-sm font-semibold text-[#2E9E3F]" data-testid="login-resent">Verification email sent — check your inbox.</p>
                : <button type="button" onClick={resend} data-testid="login-resend-button"
                    className="text-sm font-semibold text-[#2E2E2E] underline hover:text-[#3BB54A]">Resend verification email</button>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full h-14 rounded-full bg-[#3BB54A] text-white font-semibold hover:bg-[#2E9E3F] hover:text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy && <CircleNotch size={20} className="animate-spin" />}
              Log in
            </button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground">
            New here?{" "}
            <Link to="/register" className="font-semibold text-[#2E2E2E] hover:underline" data-testid="go-register-link">
              Create an account
            </Link>
          </p>

          <div className="mt-6"><InstallPrompt /></div>
        </div>
      </div>
    </div>
  );
}
